import { Box, Button, Divider, IconButton, Paper, Typography } from "@mui/material";
import Add from "@mui/icons-material/Add";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import Today from "@mui/icons-material/Today";
import moment from "moment";
import { useAsync2 } from "nate-react-api-helpers";
import { ReactNode, useRef, useState } from "react";
import { Task } from "../../../api/Tasks";
import { api } from "../../../api/API";
import { dateFormat, useToday } from "../../../misc/dates";
import { useTaskFeed } from "../../../misc/Feed";
import { Hint } from "../../../misc/Hint";
import { PaletteGate } from "../../../misc/PaletteGate";
import { useUrlState } from "../../../misc/urlParams";
import { usePhone } from "../../../misc/usePhone";
import { useWorkspace } from "../../../misc/Workspace";
import { parseAnchor } from "../calendar/range";
import { TaskChainDialog } from "../chain/TaskChainDialog";
import { groupDayTasks } from "../dayGroups";
import { DayWeekToggle } from "../DayWeekToggle";
import { NewTaskButton } from "../NewTaskButton";
import { NewTaskFab } from "../NewTaskFab";
import { useOpenTask } from "../taskUrl";
import { useCompleteTask } from "../useCompleteTask";
import { useSwipeNudge } from "./useSwipeNudge";
import { DayTaskRow } from "./DayTaskRow";
import { MobileTaskEditor } from "./MobileTaskEditor";
import { ProjectDrawer } from "./ProjectDrawer";
import { SortableDayTasks } from "./SortableDayTasks";

// dayLabel names the day the way someone reading it on a phone would, and falls back
// to the date itself once it's far enough out that a name stops meaning anything
function dayLabel(date: moment.Moment) {
    const days = date.clone().startOf("day").diff(moment().startOf("day"), "days");

    if (days === 0) return "Today";
    if (days === 1) return "Tomorrow";
    if (days === -1) return "Yesterday";
    return date.format("ddd, MMM D");
}

export function TaskDayPage() {
    // the url says which day is on screen and which task is open, so a link opens the
    // same thing and the back button walks out of it
    const url = useUrlState();
    const date = parseAnchor(url.text("date"));
    const opened = useOpenTask();
    const taskId = opened.id;
    const [chain, setChain] = useState(false);
    const ws = useWorkspace();
    const phone = usePhone();

    const day = moment(date, dateFormat);
    const isToday = day.isSame(moment(), "day");

    // the server only knows what zone the reader is in because this says so
    const today = useToday();

    // the server pushes the list again after every change, so nothing here reloads
    const list = useTaskFeed({
        dueFrom: date,
        dueTo: date,
        today: today,
        includeCompletedChecklist: true,
        team: ws.team || undefined,
    });

    // the list already carries the task a row opens, so tapping one costs no fetch
    const listed = taskId ? (list.tasks.find(task => task.id === taskId) || null) : null;

    // a task reached by link, or one that's moved off the day on screen, isn't in the
    // list to read from
    const fetched = useAsync2(async (input: { id: number }) =>
        input.id ? api.tasks.get({ id: input.id }) : null,
        { id: listed ? 0 : taskId }, [listed ? 0 : taskId]);

    // a new task is due on the day on screen
    const found: Partial<Task> | null = taskId ? (listed || fetched.result) : (opened.isNew ? { dueDate: date } : null);

    // the list is re-read whenever the day on screen changes, and a task edited onto
    // another day drops out of the one it was read from. The editor holds its place
    // through the gap rather than closing on what's being typed into it.
    const held = useRef<Partial<Task> | null>(null);
    if (found || !taskId) held.current = found;

    const editing = found || held.current;

    const toggle = useCompleteTask();

    const openTask = opened.open;
    const closeTask = opened.close;

    // stepping through the days replaces rather than pushes, so the back button walks
    // out of the page instead of back through every day on the way in
    const step = (amount: number) => url.set({
        date: day.clone().add(amount, "day").format(dateFormat),
    }, { replace: true });

    // what's been ticked off goes above what's left, and a task carried in from a day
    // that has passed goes below both (see CalendarDay)
    const { done, open, carried } = groupDayTasks(list.tasks);
    const openCount = list.tasks.filter(task => !task.completedAt).length;

    // the first open row shows that it can be swiped, now and then, until the user has
    const nudge = useSwipeNudge(phone);
    const nudgeId = nudge && open.length > 0 ? open[0].id : 0;

    const renderTask = (task: Task, grip?: ReactNode) => (
        <DayTaskRow
            key={task.id}
            task={task}
            grip={grip}
            disabled={toggle.loading}
            onClick={() => openTask(task)}
            onToggle={checked => toggle.callback({ id: task.id, completed: checked })}
            nudge={nudgeId === task.id}
        />
    );

    return (
        <PaletteGate>
            <Box sx={{ pb: 4 }}>
                {/* the day stays named while the list scrolls under it */}
                <Paper elevation={0} square sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1,
                    py: 1,
                    borderBottom: 1,
                    borderColor: "divider",
                }}>
                    <IconButton disabled={isToday} aria-label="Go to today"
                                onClick={() => url.set({ date: moment().format(dateFormat) }, { replace: true })}>
                        <Today />
                    </IconButton>

                    <IconButton onClick={() => step(-1)} aria-label="Previous day">
                        <ChevronLeft />
                    </IconButton>

                    <Box sx={{ flex: 1, minWidth: 0, textAlign: "center" }}>
                        <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                            {dayLabel(day)}
                        </Typography>
                        <Typography variant="caption" component="div" noWrap color="text.secondary">
                            {day.format("MMM D, YYYY")}
                            {openCount > 0 && " · " + openCount + " open"}
                        </Typography>
                    </Box>

                    <IconButton onClick={() => step(1)} aria-label="Next day">
                        <ChevronRight />
                    </IconButton>

                    {/* on a phone, a task is added with the floating button instead */}
                    {phone ? (
                        <DayWeekToggle value="day" date={date} />
                    ) : (
                        <>
                            {/* set off from the arrows, so stepping through the days doesn't
                                land on it by mistake */}
                            <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />
                            <NewTaskButton onTask={() => openTask({ dueDate: date })} onChain={() => setChain(true)} />
                            <div />
                        </>
                    )}
                </Paper>

                {/* the phone has no sidebar, so the team hint shows here instead */}
                {phone && !ws.loading && ws.teams.every(team => team.personal) && (
                    <Box sx={{ px: 1, pt: 1 }}>
                        <Hint flag="hint-team">
                            To share tasks with other people, tap the team name at the top and
                            select New team.
                        </Hint>
                    </Box>
                )}

                <Box sx={{ px: 1, py: 1, "&:empty": { display: "none" } }}>
                    {list.LoadingOrErrorElement}
                    {fetched.LoadingOrErrorElement}
                </Box>

                <Paper variant="outlined" square sx={{ borderTop: 0, borderLeft: 0, borderRight: 0 }}>
                    {done.map(task => renderTask(task))}

                    {/* only the day's own open tasks take part in its order */}
                    <SortableDayTasks tasks={open} renderRow={renderTask} />

                    {list.tasks.length === 0 && !list.loading && (
                        <Box sx={{ p: 4, textAlign: "center" }}>
                            <Typography variant="body2" color="text.secondary">Nothing due</Typography>
                            {phone ? (
                                <Button variant="outlined" startIcon={<Add />} sx={{ mt: 1.5 }}
                                        onClick={() => openTask({ dueDate: date })}>
                                    Add a task to this day
                                </Button>
                            ) : (
                                <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                                    Click New task to add a task to this day.
                                </Typography>
                            )}
                        </Box>
                    )}

                    {carried.length > 0 && (
                        // the group needs naming, since a carried task sits in a day that
                        // isn't the one it's due on
                        <Typography variant="caption" component="div" sx={{
                            px: 1.5, pt: 1.5, pb: 0.5,
                            color: "text.disabled",
                            fontSize: 11, fontWeight: 600, letterSpacing: "0.06em",
                            textTransform: "uppercase",
                        }}>
                            From previous
                        </Typography>
                    )}

                    {carried.map(task => renderTask(task))}
                </Paper>

                {phone && <NewTaskFab onClick={() => openTask({ dueDate: date })} />}
                {phone && <ProjectDrawer dueFrom={date} dueTo={date} today={today} />}

                {chain && (
                    <TaskChainDialog
                        onClose={() => setChain(false)}
                        onCreated={id => {
                            setChain(false);
                            openTask({ id: id });
                        }}
                    />
                )}

                {editing && (
                    <MobileTaskEditor
                        key={editing.id || "new"}
                        value={editing}
                        onClose={closeTask}
                        onSaved={task => {
                            // a new task lands on the day it was created for, so follow it
                            // there rather than leaving the reader on a day it isn't on
                            if (task.dueDate && task.dueDate !== date) {
                                url.set({ date: task.dueDate }, { replace: true });
                            }
                        }}
                    />
                )}
            </Box>
        </PaletteGate>
    );
}
